import DOMPurify from "dompurify";
import {
  Image as ImageIcon, Video, FileType2, FileText,
  MessageSquare, Phone, Link2,
} from "lucide-react";

export type Button_ = { type: "URL" | "PHONE_NUMBER" | "QUICK_REPLY"; text: string; url?: string; phoneNumber?: string };

export type TemplateForm = {
  name: string;
  category: "MARKETING" | "UTILITY" | "AUTHENTICATION";
  language: string;
  headerType: "TEXT" | "IMAGE" | "VIDEO" | "DOCUMENT" | "none";
  headerContent: string;
  bodyText: string;
  footerText: string;
  buttons: Button_[];
  variableCount: number;
  notes: string;
};

const HEADER_ICONS: Record<string, any> = {
  IMAGE: ImageIcon, VIDEO: Video, DOCUMENT: FileType2, TEXT: FileText,
};

/**
 * Preview imersivo no estilo WhatsApp dark.
 * Renderiza marcação `*bold*`, `_italic_`, `~strike~`, e {{N}} variáveis em verde.
 */
export function WhatsAppPreview({ form }: { form: TemplateForm }) {
  const renderBody = (text: string) => {
    return text
      .replace(/\*(.+?)\*/g, "<strong>$1</strong>")
      .replace(/_(.+?)_/g, "<em>$1</em>")
      .replace(/~(.+?)~/g, "<del>$1</del>")
      .replace(/\{\{(\d+)\}\}/g, '<span class="text-emerald-600 font-medium">{{$1}}</span>')
      .replace(/\n/g, "<br/>");
  };

  const HeaderIcon = form.headerType !== "none" ? HEADER_ICONS[form.headerType] : null;

  return (
    <div className="rounded-2xl overflow-hidden border shadow-sm bg-[#0b141a]">
      {/* Header da conversa */}
      <div className="flex items-center gap-2.5 px-3 py-2.5 bg-[#202c33]">
        <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white text-sm font-semibold">
          E
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-white truncate">Empresa</p>
          <p className="text-[11px] text-white/60">online</p>
        </div>
      </div>

      {/* Área de mensagens com pattern */}
      <div
        className="px-3 py-4 min-h-[300px] relative"
        style={{
          backgroundColor: "#0b141a",
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.025'%3E%3Cpath d='M40 0L40 80M0 40L80 40'/%3E%3C/g%3E%3C/svg%3E")`,
        }}
      >
        <div className="bg-[#202c33] rounded-lg overflow-hidden shadow-md">
          {/* Header de mídia */}
          {form.headerType === "IMAGE" && (
            form.headerContent ? (
              <img src={form.headerContent} alt="" className="w-full max-h-48 object-cover" />
            ) : (
              <div className="bg-slate-700 h-32 flex items-center justify-center text-slate-400 text-xs gap-2">
                <ImageIcon className="h-5 w-5" />
                Imagem
              </div>
            )
          )}
          {form.headerType === "VIDEO" && (
            <div className="bg-slate-700 h-32 flex items-center justify-center text-slate-400 text-xs gap-2">
              <Video className="h-5 w-5" />
              {form.headerContent ? form.headerContent.split("/").pop() : "Vídeo"}
            </div>
          )}
          {form.headerType === "DOCUMENT" && (
            <div className="m-2 p-3 rounded-md bg-slate-700/50 flex items-center gap-2.5">
              <FileType2 className="h-7 w-7 text-slate-300 shrink-0" />
              <div className="text-xs text-slate-200 truncate">
                {form.headerContent ? form.headerContent.split("/").pop() : "Documento.pdf"}
              </div>
            </div>
          )}
          {form.headerType === "TEXT" && form.headerContent && (
            <div className="px-3 pt-2.5 font-bold text-sm text-white">{form.headerContent}</div>
          )}

          {/* Corpo */}
          <div className="px-3 py-2">
            {form.bodyText ? (
              <p
                className="text-sm text-white/90 leading-relaxed whitespace-pre-wrap"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderBody(form.bodyText)) }}
              />
            ) : (
              <p className="text-sm text-white/30 italic">Corpo da mensagem aparece aqui...</p>
            )}
            {form.footerText && (
              <p className="text-[11px] text-white/40 mt-2">{form.footerText}</p>
            )}
            <div className="text-right -mb-0.5">
              <span className="text-[10px] text-white/40">12:00</span>
              <span className="text-[10px] text-sky-400 ml-0.5">✓✓</span>
            </div>
          </div>

          {/* Botões */}
          {form.buttons.length > 0 && (
            <div className="border-t border-white/10 divide-y divide-white/10">
              {form.buttons.map((btn, i) => {
                const Icon = btn.type === "URL" ? Link2 : btn.type === "PHONE_NUMBER" ? Phone : MessageSquare;
                return (
                  <div key={i} className="px-3 py-2 text-center text-sm text-sky-400 font-medium flex items-center justify-center gap-1.5">
                    <Icon className="h-3.5 w-3.5" />
                    <span className="truncate">{btn.text || `Botão ${i + 1}`}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {HeaderIcon && form.headerType !== "TEXT" && form.headerType !== "none" && !form.headerContent && (
          <p className="text-[11px] text-white/40 text-center mt-3">
            ↑ Adicione um arquivo para ver o preview real
          </p>
        )}
      </div>
    </div>
  );
}
